import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { UserRoleBadges } from "@/components/UserRoleBadges";
import { User, LogOut } from "lucide-react";
import type { UserRole } from "@/api/types/user-management.types";
import { menuItems } from "@/config/user-menu.config";

interface UserDropdownProps {
  align?: "start" | "center" | "end";
  variant?: "default" | "cms"; // cms variant shows different navigation options
  className?: string;
}

export const UserDropdown = ({
  align = "end",
  variant = "default",
  className
}: UserDropdownProps) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!user) return null;

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getInitials = (firstName?: string, lastName?: string) => {
    if (!firstName) return "U";
    const initials = lastName
      ? `${firstName[0]}${lastName[0]}`
      : firstName.slice(0, 2);
    return initials.toUpperCase();
  };

  const getUserFullName = () => {
    return `${user.firstName || ""} ${user.lastName || ""}`.trim();
  };

  const hasRole = (requiredRoles: UserRole[]) => {
    if (!user.roles) return false;
    return requiredRoles.some(role => user.roles!.includes(role));
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className={`relative h-10 w-10 rounded-full ${className}`}>
          <Avatar className="h-10 w-10">
            <AvatarFallback className="bg-primary text-primary-foreground">
              {getInitials(user.firstName, user.lastName)}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" align={align} forceMount>
        {/* User Info Header with Role Badges */}
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-2">
            <p className="text-sm font-medium leading-none">{getUserFullName()}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user.email}
            </p>
            {/* Role Badges */}
            {user.roles && user.roles.length > 0 && (
              <UserRoleBadges roles={user.roles} size="sm" className="pt-1" />
            )}
          </div>
        </DropdownMenuLabel>

        {/* Menu Items - Rendered based on user roles */}
        <DropdownMenuSeparator />
        {menuItems.map((item, index) => {
          if (!hasRole(item.roles)) return null;
          const Icon = item.icon;
          return (
            <DropdownMenuItem key={index} asChild>
              <button
                onClick={() => navigate(item.path)}
                className="w-full cursor-pointer flex items-center"
              >
                <Icon className="me-2 h-4 w-4" />
                {t(item.labelKey)}
              </button>
            </DropdownMenuItem>
          );
        })}

        {/* Profile - Always visible */}
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <button
            onClick={() => navigate("/profile")}
            className="w-full cursor-pointer flex items-center"
          >
            <User className="me-2 h-4 w-4" />
            {t("nav.profile")}
          </button>
        </DropdownMenuItem>

        {/* Logout Section */}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="me-2 h-4 w-4" />
          {t("common.logout")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};